import type { WaitlistDepth as WaitlistDepthData } from '@salonomy/types';
import { Pill } from '@salonomy/ui';
import { useWaitlistDepthFor } from '../../data/waitlist.js';

/**
 * WS7 phase 4 — waitlist DEPTH badge for the diary header. Shows how many clients are waiting for a
 * slot on the viewed day, plus how many currently hold an offer from a freed slot. Self-contained:
 * pass a `date` and it reads `GET /v1/waitlist/depth` itself, so the diary can mount it with just the
 * date it's already showing (Integrate wires it into the diary header — this file owns no diary code).
 *
 * Sealed-book aware by construction: the depth endpoint is RLS-scoped, so a chair_renter's badge
 * counts only their own waiting entries, an owner/manager's counts the whole floor — no extra logic.
 */

export function WaitlistDepthBadge({
  date,
  onClick,
}: {
  /** The salon-wall day the diary is showing ("YYYY-MM-DD"). */
  date: string;
  /** Optional: open the WaitlistPanel for this date (the diary passes a handler that does so). */
  onClick?: () => void;
}) {
  const depth = useWaitlistDepthFor(date);
  return <WaitlistDepthInline depth={depth} onClick={onClick} />;
}

/**
 * Presentational variant for callers that already hold the depth row (e.g. a week strip rendering a
 * row per day from a single `useWaitlistDepth()` fetch — avoids one query per cell).
 */
export function WaitlistDepthInline({
  depth,
  onClick,
}: {
  depth: WaitlistDepthData | null;
  onClick?: () => void;
}) {
  const waiting = depth?.waiting ?? 0;
  const offered = depth?.offered ?? 0;

  // Nothing waiting and nothing offered → render nothing (the diary header stays clean on quiet days).
  if (waiting === 0 && offered === 0) return null;

  const label =
    waiting > 0
      ? `${waiting} waiting${offered > 0 ? ` · ${offered} offered` : ''}`
      : `${offered} offered`;

  const pill = (
    <Pill tone={offered > 0 ? 'cobalt' : 'soft'} className="mono">
      {label}
    </Pill>
  );

  if (!onClick) return pill;
  return (
    <button
      type="button"
      onClick={onClick}
      title="View the waitlist for this day"
      aria-label={`${label} on the waitlist — open the waitlist`}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
    >
      {pill}
    </button>
  );
}
